import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, createVNode as _createVNode, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, Fragment as _Fragment, normalizeClass as _normalizeClass, resolveComponent as _resolveComponent, withCtx as _withCtx } from "vue"

const _hoisted_1 = ["aria-busy"]
const _hoisted_2 = { class: "manager-residents-ant__action-bar" }
const _hoisted_3 = ["innerHTML"]
const _hoisted_4 = { class: "manager-residents-ant__table-wrapper personal-area-table-wrapper" }
const _hoisted_5 = {
  key: 0,
  class: "manager-residents-ant__list--empty"
}
const _hoisted_6 = { class: "manager-residents-ant__list--empty-title" }
const _hoisted_7 = {
  key: 0,
  class: "manager-residents-ant__list--empty-description"
}
const _hoisted_8 = {
  key: 2,
  class: "manager-residents-ant__email"
}

import { computed, onMounted, ref } from 'vue';
import type { TableColumnsType } from 'ant-design-vue';
import FilterIcon from '@/js/components/icons/FilterIcon.vue';
import { formatMatchCountSummary } from '@/js/utils/search-highlight';
import SearchInput from '../SearchInput.vue';
import Pagination from '../Pagination.vue';
import ResidentDetailView from './ResidentDetailView.vue';
import ResidentsFilterDrawer from './ResidentsFilterDrawer.vue';
import CreateResidentDrawer from './CreateResidentDrawer.vue';
import {
  fetchManagerResidentsFilter,
  fetchManagerResidentsList,
} from './manager-residents-api';
import type {
  ManagerResidentRow,
  ManagerResidentsClusterOption,
  ManagerResidentsFilter,
  ManagerResidentsNav,
  ManagerResidentStatusOption,
} from './types';

const SEARCH_MIN_LENGTH = 3;

export default /*@__PURE__*/_defineComponent({
  __name: 'index',
  setup(__props) {

const TABLE_SCROLL = { x: 1250, y: 500 };

const columns: TableColumnsType<ManagerResidentRow> = [
  {
    title: '#',
    key: 'id',
    dataIndex: 'id',
    width: 72,
  },
  {
    title: 'Компания',
    key: 'company',
    dataIndex: 'company',
    width: 220,
  },
  {
    title: 'Кластер',
    key: 'cluster',
    dataIndex: 'cluster',
    width: 180,
  },
  {
    title: 'Руководитель',
    key: 'manager',
    dataIndex: 'manager',
    width: 180,
  },
  {
    title: 'Телефон',
    key: 'phone',
    dataIndex: 'phone',
    width: 170,
  },
  {
    title: 'Почта',
    key: 'email',
    dataIndex: 'email',
    width: 200,
  },
  {
    title: 'Статус',
    key: 'status',
    dataIndex: 'status',
    width: 170,
  },
];

const loading = ref(false);
const errorMessage = ref('');
const items = ref<ManagerResidentRow[]>([]);
const clusters = ref<ManagerResidentsClusterOption[]>([]);
const nav = ref<ManagerResidentsNav>({
  totalCount: 0,
  pageSize: 10,
  currentPage: 1,
  pageCount: 1,
});
const searchQuery = ref('');
const appliedSearchQuery = ref('');
const currentPage = ref(1);
const filterOpen = ref(false);
const appliedFilter = ref<ManagerResidentsFilter>({});
const selectedResident = ref<ManagerResidentRow | null>(null);
const statusOptions = ref<{ value: string; label: string }[]>([]);
const statusOptionsRaw = ref<ManagerResidentStatusOption[]>([]);
const createDrawerOpen = ref(false);

const isFilterApplied = computed(() =>
  Boolean(appliedFilter.value.clusterId || appliedFilter.value.statusId),
);

const hasActiveSearch = computed(() => appliedSearchQuery.value.trim().length >= SEARCH_MIN_LENGTH);

const searchSummaryMarkup = computed(() =>
  formatMatchCountSummary(nav.value.totalCount, appliedSearchQuery.value.trim()),
);

const shouldShowTable = computed(() => loading.value || nav.value.totalCount > 0);

const shouldShowEmpty = computed(
  () => !loading.value && nav.value.totalCount === 0 && !hasActiveSearch.value,
);

const emptyState = computed(() => {
  if (errorMessage.value) {
    return {
      title: errorMessage.value,
      description: '',
    };
  }

  if (isFilterApplied.value) {
    return {
      title: '0 результатов',
      description: 'Измените значения в фильтре.',
    };
  }

  return {
    title: 'Резидентов нет',
    description: '',
  };
});

function getStatusModifier(statusName?: string): string {
  const normalized = (statusName ?? '').toLowerCase();

  if (normalized.includes('актив')) return 'active';
  if (normalized.includes('исключ')) return 'excluded';
  if (normalized.includes('приостан') || normalized.includes('рассмотр')) return 'suspended';

  return 'default';
}

async function loadResidents(page = currentPage.value, query = appliedSearchQuery.value) {
  loading.value = true;
  errorMessage.value = '';

  try {
    const data = await fetchManagerResidentsList({
      q: query,
      page,
      filter: appliedFilter.value,
    });

    items.value = data.items;
    nav.value = data.nav;
    clusters.value = data.clusters;
    currentPage.value = data.nav.currentPage;
  } catch (error) {
    items.value = [];
    nav.value = {
      totalCount: 0,
      pageSize: 10,
      currentPage: 1,
      pageCount: 1,
    };
    errorMessage.value =
      error instanceof Error ? error.message : 'Не удалось загрузить список резидентов';
  } finally {
    loading.value = false;
  }
}

function handleSearch(query: string) {
  appliedSearchQuery.value = query;
  currentPage.value = 1;
  void loadResidents(1, query);
}

function handlePageChange(page: number) {
  currentPage.value = page;
  void loadResidents(page);
}

function handleFilterApply(filter: ManagerResidentsFilter) {
  appliedFilter.value = filter;
  currentPage.value = 1;
  void loadResidents(1);
}

function getRowProps(record: ManagerResidentRow) {
  return {
    onClick: () => {
      selectedResident.value = record;
    },
  };
}

function handleDetailBack() {
  selectedResident.value = null;
}

function handleDetailUpdated() {
  void loadResidents(currentPage.value);
}

function handleResidentCreated() {
  void loadResidents(1);
}

onMounted(async () => {
  try {
    const filterData = await fetchManagerResidentsFilter();
    clusters.value = filterData.clusters;
    statusOptions.value = filterData.statuses.map((item) => ({
      value: item.id,
      label: item.name,
    }));
    statusOptionsRaw.value = filterData.statuses;
  } catch {
    statusOptions.value = [];
    statusOptionsRaw.value = [];
  }

  void loadResidents(1);
});

return (_ctx: any,_cache: any) => {
  const _component_a_table = _resolveComponent("a-table")!

  return (selectedResident.value)
    ? (_openBlock(), _createBlock(ResidentDetailView, {
        key: 0,
        resident: selectedResident.value,
        clusters: clusters.value,
        onBack: handleDetailBack,
        onUpdated: handleDetailUpdated
      }, null, 8, ["resident", "clusters"]))
    : (_openBlock(), _createElementBlock("div", {
        key: 1,
        class: _normalizeClass(["manager-residents-ant", { 'is-loading': loading.value }]),
        "aria-busy": loading.value ? 'true' : undefined
      }, [
        _cache[9] || (_cache[9] = _createElementVNode("h2", { class: "manager-residents-ant__title" }, "Резиденты", -1)),
        _createElementVNode("div", _hoisted_2, [
          _createVNode(SearchInput, {
            modelValue: searchQuery.value,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((searchQuery).value = $event)),
            placeholder: "Найти...",
            onSearch: handleSearch
          }, null, 8, ["modelValue"]),
          _createElementVNode("button", {
            type: "button",
            class: "btn btn-secondary btn-medium",
            onClick: _cache[1] || (_cache[1] = ($event: any) => (filterOpen.value = true))
          }, [
            _createVNode(FilterIcon, { filled: isFilterApplied.value }, null, 8, ["filled"]),
            _cache[6] || (_cache[6] = _createElementVNode("span", null, "Фильтр", -1))
          ]),
          _createElementVNode("button", {
            type: "button",
            class: "btn btn-primary btn-medium",
            onClick: _cache[2] || (_cache[2] = ($event: any) => (createDrawerOpen.value = true))
          }, [...(_cache[7] || (_cache[7] = [
            _createElementVNode("span", null, "Создать резидента", -1)
          ]))])
        ]),
        (hasActiveSearch.value)
          ? (_openBlock(), _createElementBlock("p", {
              key: 0,
              class: "search-summary manager-residents-ant__search-summary",
              innerHTML: searchSummaryMarkup.value
            }, null, 8, _hoisted_3))
          : _createCommentVNode("", true),
        _createElementVNode("div", _hoisted_4, [
          (shouldShowEmpty.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                _createElementVNode("div", _hoisted_6, _toDisplayString(emptyState.value.title), 1),
                (emptyState.value.description)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_7, _toDisplayString(emptyState.value.description), 1))
                  : _createCommentVNode("", true)
              ]))
            : (shouldShowTable.value)
              ? (_openBlock(), _createBlock(_component_a_table, {
                  key: 1,
                  class: "manager-residents-ant__table",
                  columns: columns,
                  bordered: true,
                  "data-source": items.value,
                  loading: loading.value,
                  pagination: false,
                  scroll: TABLE_SCROLL,
                  "custom-row": getRowProps,
                  "row-key": "id"
                }, {
                  bodyCell: _withCtx(({ column, record }) => [
                    (column.key === 'id')
                      ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                          _createTextVNode(_toDisplayString(record.id), 1)
                        ], 64))
                      : (column.key === 'company')
                        ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                            _createTextVNode(_toDisplayString(record.company), 1)
                          ], 64))
                        : (column.key === 'email')
                          ? (_openBlock(), _createElementBlock("span", _hoisted_8, _toDisplayString(record.email), 1))
                          : (column.key === 'status')
                            ? (_openBlock(), _createElementBlock("span", {
                                key: 3,
                                class: _normalizeClass(["manager-residents-ant__status", `manager-residents-ant__status--${getStatusModifier(record.status)}`])
                              }, [
                                _cache[8] || (_cache[8] = _createElementVNode("span", {
                                  class: "manager-residents-ant__status-dot",
                                  "aria-hidden": "true"
                                }, null, -1)),
                                _createTextVNode(" " + _toDisplayString(record.status || '—'), 1)
                              ], 2))
                            : _createCommentVNode("", true)
                  ]),
                  _: 1
                }, 8, ["data-source", "loading"]))
              : _createCommentVNode("", true)
        ]),
        (nav.value.totalCount > nav.value.pageSize)
          ? (_openBlock(), _createBlock(Pagination, {
              key: 1,
              modelValue: currentPage.value,
              "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((currentPage).value = $event)),
              class: "manager-residents-ant__pagination personal-area-table-pagination",
              "total-count": nav.value.totalCount,
              "page-size": nav.value.pageSize,
              onChange: handlePageChange
            }, null, 8, ["modelValue", "total-count", "page-size"]))
          : _createCommentVNode("", true),
        _createVNode(ResidentsFilterDrawer, {
          open: filterOpen.value,
          "onUpdate:open": _cache[4] || (_cache[4] = ($event: any) => ((filterOpen).value = $event)),
          clusters: clusters.value,
          "status-options": statusOptions.value,
          filter: appliedFilter.value,
          onApply: handleFilterApply
        }, null, 8, ["open", "clusters", "status-options", "filter"]),
        _createVNode(CreateResidentDrawer, {
          open: createDrawerOpen.value,
          "onUpdate:open": _cache[5] || (_cache[5] = ($event: any) => ((createDrawerOpen).value = $event)),
          clusters: clusters.value,
          "status-options": statusOptionsRaw.value,
          onCreated: handleResidentCreated
        }, null, 8, ["open", "clusters", "status-options"])
      ], 10, _hoisted_1))
}
}

})